// ARQUIVO 100% COMPLETO E CORRIGIDO
import React, { useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import useData from '../../hooks/useData';
import useToast from '../../hooks/useToast';
import { CloseIcon, SaveIcon, DeleteIcon, ResetIcon, ChevronDownIcon } from '../ui/Icons';
import useClickOutside from '../../hooks/useClickOutside';
import SaveThemeModal from '../modals/SaveThemeModal';
import useDraggableResizable from '../../hooks/useDraggableResizable';

const AccordionSection = ({ title, children, isOpen, onClick }) => (
    <div className="border-b border-white/10">
        <button onClick={onClick} className="w-full flex justify-between items-center p-3 text-left">
            <h4 className="font-semibold text-sm text-gray-300">{title}</h4>
            <ChevronDownIcon size={16} className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
            <div className="p-3 pt-0">
                {children}
            </div>
        </div>
    </div>
);

const ThemeManager = ({ themes, onSelect, onDelete }) => {
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });

    const openMenu = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setPosition({ top: rect.bottom + 8, left: rect.left });
            setIsOpen(true);
        }
    };

    useClickOutside(dropdownRef, () => setIsOpen(false));

    return (
        <div className="relative">
            <button ref={buttonRef} onClick={openMenu} className="w-full flex justify-between items-center bg-black/20 p-2 rounded-md text-xs">
                <span>Carregar Tema</span>
                <ChevronDownIcon size={16} />
            </button>
            {isOpen && createPortal(
                <div ref={dropdownRef} style={{ top: `${position.top}px`, left: `${position.left}px` }} className="fixed w-52 bg-gray-900 border border-white/10 rounded-lg shadow-xl z-[60] max-h-48 overflow-y-auto animate-fade-in">
                    {themes.map(theme => (
                        <div key={theme.id} className="group flex justify-between items-center text-left text-sm p-2 hover:bg-white/10">
                            <button onClick={() => { onSelect(theme.settings); setIsOpen(false); }} className="flex-grow text-left text-white">
                                {theme.name}
                            </button>
                            <button onClick={() => onDelete(theme.id)} className="p-1 text-red-500 opacity-0 group-hover:opacity-100">
                                <DeleteIcon size={16} />
                            </button>
                        </div>
                    ))}
                    {themes.length === 0 && <p className="p-2 text-xs text-gray-400 text-center">Nenhum tema salvo.</p>}
                </div>,
                document.body
            )}
        </div>
    );
};

const DesignToolbar = ({ onClose, onSaveThemeRequest }) => {
    const { 
        activeDesignSettings: settings, 
        handleApplyDesignSettings, 
        handleResetDesignSettings,
        designThemes,
        handleDeleteDesignTheme
    } = useData();
    const { showToast } = useToast();

    const initialSize = useMemo(() => ({ width: 300, height: 480 }), []);
    const initialPos = useMemo(() => ({
        x: (window.innerWidth - initialSize.width) / 2,
        y: (window.innerHeight - initialSize.height) / 2,
    }), [initialSize]);

    const { elementRef, position, size } = useDraggableResizable(
        initialPos, 
        initialSize, 
        null,
        { minW: 280, minH: 300 }
    );

    const [openSection, setOpenSection] = useState('Temas');
    const handleToggleSection = (sectionName) => {
        setOpenSection(openSection === sectionName ? null : sectionName);
    };
    
    const handleSettingChange = (key, value) => {
        const newSettings = { ...settings, [key]: value };
        handleApplyDesignSettings(newSettings);
    };

    return (
        <div 
            ref={elementRef}
            style={{ 
                top: `${position.y}px`, 
                left: `${position.x}px`,
                width: `${size.width}px`,
                height: `${size.height}px`,
            }}
            className="fixed z-50 bg-gray-800/80 backdrop-blur-lg border border-white/20 rounded-xl shadow-2xl text-white animate-scale-in flex flex-col"
        >
            <div data-action="drag" className="flex-shrink-0 flex justify-between items-center p-3 border-b border-white/10 cursor-move touch-none">
                <h3 className="font-bold text-lg select-none">Editar Design</h3>
                <button onClick={onClose} className="p-1 rounded-full hover:bg-white/10 cursor-pointer"><CloseIcon size={20}/></button>
            </div>

            <div className="flex-grow overflow-y-auto">
                <AccordionSection title="Temas" isOpen={openSection === 'Temas'} onClick={() => handleToggleSection('Temas')}>
                    <div className="space-y-2 bg-black/20 p-3 rounded-lg">
                        <ThemeManager 
                            themes={designThemes} 
                            onSelect={(themeSettings) => {
                                handleApplyDesignSettings(themeSettings);
                                showToast("Tema aplicado!");
                            }}
                            onDelete={(themeId) => {
                                handleDeleteDesignTheme(themeId);
                                showToast("Tema excluído.", "error");
                            }}
                        />
                        <div className="flex gap-2">
                            <button onClick={onSaveThemeRequest} className="flex-1 text-xs flex items-center justify-center gap-1 p-2 bg-indigo-500/80 hover:bg-indigo-500 rounded-md"><SaveIcon size={14}/> Salvar</button>
                            <button onClick={() => { handleResetDesignSettings(); showToast("Design resetado para o padrão."); }} className="flex-1 text-xs flex items-center justify-center gap-1 p-2 bg-white/10 hover:bg-white/20 rounded-md"><ResetIcon size={14}/> Resetar</button>
                        </div>
                    </div>
                </AccordionSection>

                <AccordionSection title="Cores" isOpen={openSection === 'Cores'} onClick={() => handleToggleSection('Cores')}>
                    <div className="space-y-2">
                        <div className="flex justify-between items-center"><label className="text-xs">Texto Cabeçalho</label><input type="color" value={settings.headerTextColor || '#1f2937'} onChange={e => handleSettingChange('headerTextColor', e.target.value)} className="w-8 h-8 bg-transparent"/></div>
                        <div className="flex justify-between items-center"><label className="text-xs">Destaque (Títulos)</label><input type="color" value={settings.accentColor} onChange={e => handleSettingChange('accentColor', e.target.value)} className="w-8 h-8 bg-transparent"/></div>
                        <div className="flex justify-between items-center"><label className="text-xs">Texto Geral</label><input type="color" value={settings.bodyTextColor} onChange={e => handleSettingChange('bodyTextColor', e.target.value)} className="w-8 h-8 bg-transparent"/></div>
                        <div className="flex justify-between items-center"><label className="text-xs">Linha da Tabela</label><input type="color" value={settings.tableLineColor} onChange={e => handleSettingChange('tableLineColor', e.target.value)} className="w-8 h-8 bg-transparent"/></div>
                        <div className="flex justify-between items-center"><label className="text-xs">Fundo do Total</label><input type="color" value={settings.totalColor} onChange={e => handleSettingChange('totalColor', e.target.value)} className="w-8 h-8 bg-transparent"/></div>
                    </div>
                </AccordionSection>

                <AccordionSection title="Tipografia" isOpen={openSection === 'Tipografia'} onClick={() => handleToggleSection('Tipografia')}>
                     <div className="space-y-4">
                        <div>
                            <label className="text-xs">Fonte Principal</label>
                            <select value={settings.fontFamily} onChange={e => handleSettingChange('fontFamily', e.target.value)} className="w-full bg-black/20 p-2 rounded-md mt-1 text-xs">
                                <option value="'Inter', sans-serif">Inter</option>
                                <option value="'Roboto', sans-serif">Roboto</option>
                                <option value="'Times New Roman', serif">Times New Roman</option>
                                <option value="'Georgia', serif">Georgia</option>
                            </select>
                        </div>
                        {/* --- INÍCIO DA ALTERAÇÃO --- */}
                        <div>
                            <label className="text-xs">Tamanho Título Principal ({settings.titleFontSize}px)</label>
                            <input type="range" min="10" max="25" value={settings.titleFontSize} onChange={e => handleSettingChange('titleFontSize', e.target.value)} className="w-full"/>
                        </div>
                        {/* --- FIM DA ALTERAÇÃO --- */}
                        <div>
                            <label className="text-xs">Tam. Título Secundário ({settings.quoteHeaderFontSize}px)</label>
                            <input type="range" min="10" max="24" value={settings.quoteHeaderFontSize} onChange={e => handleSettingChange('quoteHeaderFontSize', e.target.value)} className="w-full"/>
                        </div>
                    </div>
                </AccordionSection>
            </div>

            <div data-action="resize" className="resize-handle" />
        </div>
    );
};

export default DesignToolbar;